/**
 * RD Equipamentos (ramo 0171) — Wire Contract types.
 *
 * Authoritative source: `docs/superpowers/plans/2026-07-05-rd-equipamentos-backend.md`
 * ("Wire Contract" + "Task 3: Product configuration as typed constants" sections).
 * Keep every field in sync with the backend plan.
 */

export type RdProductType = 'individual' | 'frota' | 'lmi_unico';
export type RdModalitySlug = 'estacionario' | 'movel' | 'portatil';
export type RdStatus =
  | 'rascunho'
  | 'cotada'
  | 'aguardando_analise'
  | 'aprovada'
  | 'aceita'
  | 'emitida'
  | 'cancelada';

/**
 * Status terminais: a proposta não aceita mais nenhuma escrita.
 *
 * Espelha `FROZEN_STATUSES` do motor (`services/proposal.py`), que recusa
 * escrita nestes estados com 400. O wizard precisa da mesma regra para não
 * oferecer campos editáveis que o servidor vai rejeitar — e, mais importante,
 * porque estes estados não têm transição de saída: uma escrita que passasse
 * corromperia a apólice sem possibilidade de recálculo.
 */
export const RD_FROZEN_STATUSES: readonly RdStatus[] = ['emitida', 'cancelada'];

/** `true` quando a proposta está em um status terminal (somente leitura). */
export function isRdProposalFrozen(status?: RdStatus): boolean {
  return !!status && RD_FROZEN_STATUSES.includes(status);
}
export type RdPolicyType =
  | 'novo'
  | 'renovacao_interna'
  | 'renovacao_externa_sem_sinistro'
  | 'renovacao_interna_com_sinistro';

// ---------------------------------------------------------------------------
// Product configuration facets — served granularly via GET /config/<facet>.
// ---------------------------------------------------------------------------

export interface RdModality {
  slug: RdModalitySlug;
  name: string;
  lmiMax: number;
  minPremium: number;
  prorataBase: number;
  requiresLocation: boolean;
  /** Se a modalidade multiplica o prêmio pelo fator de agravação por idade. */
  usesAge: boolean;
}

export interface RdCoverageDef {
  slug: string;
  name: string;
  mandatory: boolean;
  automatic: boolean;
  limitRule: string | null;
  /** Se a cobertura pede período indenitário — decisão do cadastro. */
  usesIndemnityPeriod?: boolean;
}

export interface RdFranchiseRule {
  coverageSlug: string;
  /**
   * Modalidade a que a regra se aplica. AUSENTE é a regra global, que vale
   * para toda modalidade; presente, é o override daquela. Proximidade de
   * Água, por exemplo, só existe por modalidade (móvel 1,5%, estacionário e
   * portátil 1,0%) — sem este campo ela aparecia SEM franquia na tela.
   */
  modalitySlug?: RdModalitySlug;
  posType: 'percent' | 'time' | 'exempt';
  posPercent?: number;
  minValue?: number;
  minPercent?: number;
  maxValue?: number;
  timeDays?: number;
}

/**
 * Faixa de agravação por idade de uma modalidade, indexada pelo ANO de
 * fabricação (`minYear`/`maxYear` nulos = faixa aberta daquele lado). A ORDEM
 * do array é a ordem de avaliação do motor — a primeira faixa que casa vence.
 */
export interface RdAgeBand {
  minYear: number | null;
  maxYear: number | null;
  /** Multiplicador em micro: 1_000_000 = 1,00 · 1_150_000 = 1,15. */
  factor: number;
  rejects: boolean;
}

export type RdModalityAgeBands = Record<RdModalitySlug, RdAgeBand[]>;

export interface RdCommercialRule {
  maxCommission: number;
  maxDiscount: number;
  maxAdjustFactor: number;
  acceptValueCap: number;
  acceptMaxAgeYears: number;
  acceptMaxItems: number | null;
  acceptMaxValidityDays: number;
  quoteValidityDays: number;
}

export interface RdInstallmentConfig {
  maxInstallments: number;
  interest: number;
  iofFactor: number;
  allowFirstCash: boolean;
  allowFirst30d: boolean;
}

/**
 * Forma de pagamento habilitada no produto (catálogo do i4pro, servido
 * pelo BFF em `config/payment-methods`). Responde COMO se paga — não
 * confundir com `firstInstallment`, que é QUANDO vence a primeira parcela.
 */
export interface RdPaymentMethod {
  code: number;
  name: string;
}

export interface RdFrotaPlano {
  product: 'frota' | 'lmi_unico';
  slug: string;
  name: string;
  description: string;
}

export interface RdCatalogEquipment {
  code: string;
  name: string;
  modalitySlug: RdModalitySlug;
}

export interface RdAggregateProductConfig {
  modality: RdModalitySlug;
  coverageSlugs: string[];
}

// ---------------------------------------------------------------------------
// Granular config facets (GET /config/<faceta>) — one type per per-facet
// service method.
// ---------------------------------------------------------------------------

/** Coberturas disponíveis por modalidade. Espelha `RdModalityCoverages` da Portal.Api. */
export type RdModalityCoverages = Record<RdModalitySlug, string[]>;
export type RdAggregateProducts = Record<
  'frota' | 'lmi_unico',
  RdAggregateProductConfig
>;

// ---------------------------------------------------------------------------
// Proposal aggregate (embedded equipment/coverages).
// ---------------------------------------------------------------------------

export interface RdProposalCoverage {
  coverageSlug: string;
  selected: boolean;
  lmi: number;
  /** Período indenitário em meses; ausente quando não escolhido. */
  indemnityMonths?: number;
  premium?: number;
}

export interface RdProposalEquipment {
  itemId: string;
  equipmentCode: string;
  equipmentName: string;
  modalitySlug: RdModalitySlug;
  brand?: string;
  model?: string;
  /** Chassi/nº de série — comprovação em sinistro; exigido ao efetivar. */
  serialNumber?: string;
  year: number;
  value: number;
  isZeroKm: boolean;
  nfNumber?: string;
  nfDate?: string;
  nfIssuer?: string;
  /** Endereço do risco DESTE item — só preenchido quando a modalidade exige local. */
  riskCep?: string;
  riskAddress?: string;
  riskNumber?: string;
  riskComplement?: string;
  riskDistrict?: string;
  riskCity?: string;
  riskState?: string;
  itemPremium?: number;
  coverages: RdProposalCoverage[];
}

export interface RdEmissionRecord {
  protocol: string;
  integration: string;
  status: string;
  issuedAt: string;
}

/** `GET /:proposalId/transmission` — ver `transmit` no service. */
export interface RdTransmission {
  transmitted: boolean;
}

export interface RdProposal {
  proposalId: string;
  number: string;
  status: RdStatus;
  productType: RdProductType;
  policyType: RdPolicyType;
  /** Locado/arrendado/cedido a terceiros — subscrição; não altera o cálculo. */
  leasedToThirdParties?: boolean;
  previousPolicy?: string;
  insuredType: 'cnpj' | 'cpf';
  insuredDoc: string;
  insuredName: string;
  insuredSocialName?: string;
  insuredRg?: string;
  insuredRgIssuer?: string;
  insuredRgDate?: string;
  insuredPhone?: string;
  insuredLandline?: string;
  insuredEmail?: string;
  brokerName?: string;
  riskCep?: string;
  riskAddress?: string;
  riskNumber?: string;
  riskComplement?: string;
  riskDistrict?: string;
  riskCity?: string;
  riskState?: string;
  mailCep?: string;
  mailAddress?: string;
  mailNumber?: string;
  mailComplement?: string;
  mailDistrict?: string;
  mailCity?: string;
  mailState?: string;
  coverageScope: 'Território Brasileiro' | 'Mercosul';
  validityStart: string;
  validityEnd: string;
  installments?: number;
  firstInstallment?: 'cash' | '30d';
  paymentMethod?: number;
  /** Individual flow (embedded); `[]` for frota/lmi_unico. */
  equipment: RdProposalEquipment[];
  /** micro */
  commission: number;
  /** micro */
  discountFactor: number;
  riskValue?: number;
  totalLmi?: number;
  frotaPlano?: string;
  fleetCoverages?: Record<string, number>;
  /** Período indenitário do agregado (um por proposta). */
  fleetIndemnityMonths?: number;
  /** cents */
  totalPremium?: number;
  /** cents */
  totalCommission?: number;
  emission?: RdEmissionRecord;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

// ---------------------------------------------------------------------------
// Real-time quote breakdown (GET /:proposalId/quote).
// ---------------------------------------------------------------------------

export interface RdQuoteItemCoverage {
  coverageSlug: string;
  name: string;
  lmi: number;
  premium: number;
  indemnityMonths?: number;
}

export interface RdQuoteItem {
  itemId: string;
  modalitySlug: RdModalitySlug;
  status: 'ok' | 'is_acima_limite' | 'recusado_idade';
  itemPremium: number;
  minPremium: number;
  coverages: RdQuoteItemCoverage[];
  reason?: string;
}

export interface RdInstallmentOption {
  n: number;
  firstCash: number;
  first30d: number;
  interest: number;
}

export interface RdQuoteFrotaCoverage {
  slug: string;
  name: string;
  lmi: number;
  /** Período indenitário; no agregado é um só, e vem na linha que o pede. */
  indemnityMonths?: number;
}

export interface RdQuoteFrota {
  riskValue: number;
  totalLmi: number;
  /** null while no plano is selected or no bucket rate exists (frota_incompleta / sem_taxa). */
  planoSlug: string | null;
  planoName: string | null;
  planoDescription: string | null;
  coverages: RdQuoteFrotaCoverage[];
}

export interface RdQuoteError {
  itemId?: string;
  status: string;
  reason: string;
  /**
   * `false` = referral **suave**: o prêmio foi calculado e vale, a cotação
   * apenas segue para análise (ex.: valor acima do teto de aceitação
   * automática). `true` = configuração inválida que o motor recusou.
   * Discriminar por `errors.length` trata os dois como erro e mostra um
   * referral suave como falha.
   */
  blocking: boolean;
}

export interface RdQuoteResult {
  productType: RdProductType;
  totalPremium: number;
  totalCommission: number;
  minPremiumApplied: boolean;
  items: RdQuoteItem[];
  installments: RdInstallmentOption[];
  errors: RdQuoteError[];
  frota?: RdQuoteFrota;
}

// ---------------------------------------------------------------------------
// Mutation payloads (mirror the backend DTOs field-for-field).
// ---------------------------------------------------------------------------

export interface RdClientPayload {
  productType: RdProductType;
  /** Locado/arrendado/cedido a terceiros — subscrição; não altera o cálculo. */
  leasedToThirdParties?: boolean;
  insuredType: 'cnpj' | 'cpf';
  insuredDoc: string;
  insuredName: string;
  insuredSocialName?: string;
  insuredRg?: string;
  insuredRgIssuer?: string;
  insuredRgDate?: string;
  insuredPhone?: string;
  insuredLandline?: string;
  insuredEmail?: string;
  brokerName?: string;
  policyType: RdPolicyType;
  previousPolicy?: string;
  riskCep?: string;
  riskAddress?: string;
  riskNumber?: string;
  riskComplement?: string;
  riskDistrict?: string;
  riskCity?: string;
  riskState?: string;
  mailCep?: string;
  mailAddress?: string;
  mailNumber?: string;
  mailComplement?: string;
  mailDistrict?: string;
  mailCity?: string;
  mailState?: string;
  coverageScope: 'Território Brasileiro' | 'Mercosul';
  validityStart: string;
  validityEnd: string;
}

export interface RdEquipmentPayload {
  equipmentCode: string;
  year: number;
  /** cents */
  value: number;
  brand?: string;
  /** Modelo — obrigatório no formulário do Portal. */
  model?: string;
  /** Chassi/nº de série — comprovação em sinistro; exigido ao efetivar. */
  serialNumber?: string;
  isZeroKm: boolean;
  nfNumber?: string;
  nfDate?: string;
  nfIssuer?: string;
  // Endereço do risco DESTE item — só preenchido quando a modalidade exige
  // local (Estacionário). O formulário já os coletava e o hook já os
  // enviava (o `safeParse` devolve um tipo mais largo, que o TS aceita
  // atribuir aqui); o contrato do backend os declara em `RdEquipmentDto`.
  riskCep?: string;
  riskAddress?: string;
  riskNumber?: string;
  riskComplement?: string;
  riskDistrict?: string;
  riskCity?: string;
  riskState?: string;
}

/**
 * Prazo de indenização oferecível — a DURAÇÃO da indenização das coberturas
 * que a pedem. Vem do cadastro do motor, não de constante: desativar um prazo
 * no admin tira-o da tela sem deploy.
 *
 * Não confundir com `RdFranchiseRule.timeDays`, que é a CARÊNCIA: o tempo
 * antes de a indenização começar.
 */
export interface RdIndemnityPeriod {
  months: number;
  name: string;
}

export interface RdCoverageSelectionItem {
  coverageSlug: string;
  selected: boolean;
  lmi: number;
  /** Período indenitário em meses; só nas coberturas que o pedem. */
  indemnityMonths?: number;
}

export interface RdCoverageSelectionPayload {
  coverages: RdCoverageSelectionItem[];
}

export interface RdFleetPayload {
  riskValue: number;
  totalLmi: number;
  plano: string;
  fleetCoverages?: Record<string, number>;
  /** Período indenitário do agregado (um por proposta). */
  fleetIndemnityMonths?: number;
}

export interface RdParamsPayload {
  /** micro */
  commission: number;
  /** micro */
  discountFactor: number;
  installments: number;
  firstInstallment: 'cash' | '30d';
  /** `code` de `RdPaymentMethod`; omitido = não informada. */
  paymentMethod?: number;
}
